import React from "react";

import { Button } from "@newsela/angelou";
import { isEmpty, map } from "@s-libs/micro-dash";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";
import UniversalEmbedCardFooter from "@/browse/UniversalEmbedCardFooter";
import CardFooter from "@/components/CardFooter";
import DeckOfCards from "@/components/DeckOfCards";
import { contentCardContainerCss } from "@/search/SearchPage/SearchResults/SearchResults.style";
import { generateTrackingDatastreamProperties } from "@/utils/renderOulipoDataAsComponents/renderOulipoDataBlock";
import {
  cardCss,
  cardWrapRowCss,
  contentSettingsWrapRowCss,
  errorContainerWrapRowCss,
  noResultsMessageWrapRowCss,
  tryAgainButtonWrapRowCss,
} from "./WrapRow.styles";

export const propTypes = {
  /* Pass an array of content cards that should be rendered in row */
  rowMembers: PropTypes.array,
  isError: PropTypes.bool.isRequired,
  streamProperties: PropTypes.object,
  needle: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  searchId: PropTypes.string,
  contentSettingsGroups: PropTypes.array,
  currentPage: PropTypes.number.isRequired,
  totalNumberOfResultsAgathaCanReturn: PropTypes.number,
  invokeFunctionToFetchSearchResults: PropTypes.func.isRequired,
  isUniversalEmbed: PropTypes.bool,
};

const defaultProps = {
  rowMembers: [],
  searchId: "",
  isUniversalEmbed: false,
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const WrapRow = ({
  needle,
  isError,
  searchId,
  isLoading,
  isUniversalEmbed,
  rowMembers,
  currentPage,
  streamProperties,
  contentSettingsGroups,
  invokeFunctionToFetchSearchResults,
  totalNumberOfResultsAgathaCanReturn,
}) => {
  const router = useRouter();
  const { query } = router;
  const urlParameters = new URLSearchParams(query);

  const isContentSettingsMode =
    Array.isArray(contentSettingsGroups) && contentSettingsGroups.length > 0;

  /**
   * Get all the cards needed to populate a row
   * @param {object} rowMembers - Props holding responseData from parent
   * @param {object} streamProperties - Data for event tracking
   */
  const generateWrapRowElements = (rowMembers, streamProperties) => {
    const rowItems = map(rowMembers, (member) => {
      const getIndex = rowMembers.indexOf(member);
      const getContentIds = rowMembers.map((member) => member.content_id);
      const contentTypeAndId = `${member.content_type}_${member.content_id}`;
      let footer = (
        <CardFooter
          contentId={member.content_id}
          contentType={member.content_type}
          recommendationSetIds={member.recommendation_set_ids}
          veil={member.veil}
          visibilityMarkers={member.visibility_markers}
        />
      );
      if (isContentSettingsMode) {
        footer = (
          <AdminContentSettingsCardFooter
            contentId={member.content_id}
            contentSettingsGroups={contentSettingsGroups}
            visibilityMarkers={member.visibility_markers}
          />
        );
      } else if (isUniversalEmbed) {
        const requestId = query.request_id;
        const productFamily = query.product_family;
        const addOnToken = query.addOnToken;
        const courseId = query.courseId;
        const itemId = query.itemId || query.postId;
        const itemType =
          query.itemType || (query.postId ? "courseWork" : undefined);

        footer = (
          <UniversalEmbedCardFooter
            contentId={member.content_id}
            title={member.title}
            slug={member.slug}
            requestId={requestId}
            productFamily={productFamily}
            addOnToken={addOnToken}
            courseId={courseId}
            itemId={itemId}
            itemType={itemType}
          />
        );
      }

      return (
        <DeckOfCards
          {...member}
          key={member.content_id}
          dataProps={{ "data-content-type-and-content-id": contentTypeAndId }}
          onClick={() => {
            localStorage.setItem(
              "clickedContentCardOnSearchPage",
              contentTypeAndId,
            );
          }}
          streamProperties={{
            ...streamProperties,
            application: "agatha",
            count_results_retrieved: rowMembers.length,
            count_results_total: totalNumberOfResultsAgathaCanReturn,
            query_term: needle,
            request_type: "search",
            user_agent: navigator.userAgent,
            action_name: "Search",
            action_prefix: null,
            component_type: "ResultListContainer",
            description: "Search Results",
            current_url: location?.href,
            result_identifiers: getContentIds,
            layout_index: getIndex,
          }}
          css={cardCss}
          footer={footer}
          trackImpression
        />
      );
    });

    return rowItems;
  };

  const rowElements = generateWrapRowElements(
    rowMembers,
    generateTrackingDatastreamProperties(rowMembers, streamProperties),
  );

  const renderErrorMessage = () => {
    return (
      <div css={errorContainerWrapRowCss}>
        <div>Oops, there was an error loading results.</div>
        <ButtonWithTrackEvent
          onClick={() => {
            invokeFunctionToFetchSearchResults({
              urlParameters,
              page: currentPage,
              loadingNextPageOfSearchResults: true, // We're not loading *more* results, but this allows any existing
              // content-cards that appear on the page to remain on the page while
              // the client re-initiates a request for the prior failed fetch.
            });
          }}
          objectType={objectTypes.content}
          actionPrefix={actionPrefixes.initiate}
          actionName={actionNames.search}
          streamProperties={{
            description: "Search page Try Again button",
            search_id: searchId,
            search_term: needle,
          }}
          __cssFor={tryAgainButtonWrapRowCss}
        >
          Try Again
        </ButtonWithTrackEvent>
      </div>
    );
  };

  const renderNoResultsMessage = () => {
    return <div css={noResultsMessageWrapRowCss}>No results found.</div>;
  };

  return (
    <>
      <div
        css={contentCardContainerCss}
        tabIndex={-1}
        // eslint-disable-next-line react/no-unknown-property
        heap-content-row={rowMembers?.slug}
      >
        {isError && !isLoading && renderErrorMessage()}
        <div
          css={[
            cardWrapRowCss,
            isContentSettingsMode
              ? contentSettingsWrapRowCss(contentSettingsGroups.length)
              : {},
          ]}
        >
          {isError &&
            !isLoading &&
            isEmpty(rowElements) &&
            renderNoResultsMessage()}
          {!isLoading && rowElements}
        </div>
      </div>
    </>
  );
};

WrapRow.defaultProps = defaultProps;
WrapRow.propTypes = propTypes;
WrapRow.displayName = "WrapRow";
export default WrapRow;
